import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowLeft, ExternalLink, ShieldCheck } from "lucide-react";
import { useNewsItems } from "@/knowmeno/news/newsQueries";
import { LocaleSwitcher } from "@/knowmeno/marketing/LocaleSwitcher";
import { esHead } from "@/knowmeno/marketing/EsPlaceholder";

const TOPICS = [
  { id: "all", label: "Todo" },
  { id: "research", label: "Investigación" },
  { id: "treatment", label: "Tratamientos" },
  { id: "product", label: "Productos" },
  { id: "lifestyle", label: "Estilo de vida" },
];

export const Route = createFileRoute("/es/noticias")({
  head: () => esHead({
    path: "/noticias",
    title: "Noticias de menopausia | SepaMeno",
    description: "Noticias curadas en español sobre menopausia: investigación, tratamientos y productos. Fuentes médicas confiables.",
    englishPath: "/news",
  }),
  component: NoticiasPage,
});

function NoticiasPage() {
  const { items, loading } = useNewsItems(50, "es");
  const [topic, setTopic] = useState("all");
  const filtered = topic === "all" ? items : items.filter((it) => it.topic === topic);

  return (
    <div className="min-h-screen bg-background" lang="es">
      <header className="border-b">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4">
          <Link to="/es" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> SepaMeno
          </Link>
          <div className="flex items-center gap-3">
            <h1 className="font-serif text-xl">Meno Noticias</h1>
            <LocaleSwitcher />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-10">
        <h2 className="font-serif text-3xl sm:text-4xl">Lo último en menopausia</h2>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          Curado de fuentes médicas en español — AEEM, SciELO, Mayo Clinic en Español, NIH en Español, Menopausia.com.
        </p>

        <div className="mt-6 flex flex-wrap gap-2">
          {TOPICS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTopic(t.id)}
              className={`rounded-full border px-4 py-1.5 text-sm ${
                topic === t.id ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:bg-muted"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8 space-y-4">
          {loading && <p className="text-sm text-muted-foreground">Cargando…</p>}
          {!loading && filtered.length === 0 && (
            <div className="rounded-lg border bg-card p-8 text-center">
              <p className="text-muted-foreground">Aún no hay noticias en español publicadas.</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Estamos integrando fuentes en español. Vuelve pronto.
              </p>
            </div>
          )}
          {filtered.map((it) => {
            const isAffiliate = !!it.affiliate_url;
            const href = it.affiliate_url || it.source_url;
            return (
              <article key={it.id} className="rounded-lg border bg-card p-5 hover:bg-muted/30">
                <div className="flex items-start gap-4">
                  {it.image_url && (
                    <img src={it.image_url} alt="" className="h-20 w-20 shrink-0 rounded object-cover" />
                  )}
                  <div className="flex-1">
                    <a
                      href={href}
                      target="_blank"
                      rel={isAffiliate ? "nofollow sponsored noopener noreferrer" : "noopener noreferrer"}
                      className="font-serif text-lg hover:text-primary"
                    >
                      {it.title} <ExternalLink className="inline h-3.5 w-3.5" />
                    </a>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {it.source_name}
                      {it.published_at && ` · ${new Date(it.published_at).toLocaleDateString("es")}`}
                    </div>
                    {it.summary && <p className="mt-2 text-sm text-muted-foreground">{it.summary}</p>}
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <p className="mt-10 flex items-center gap-2 text-xs text-muted-foreground">
          <ShieldCheck className="h-4 w-4" /> Algunos enlaces son de afiliados. No es consejo médico — consulta con un profesional de salud calificado.
        </p>
      </main>
    </div>
  );
}
